import { Suspense, useState, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { motion, AnimatePresence } from 'motion/react';
import { isWebGLAvailable } from '@/utils/webgl';
import ShowcaseCharacter3D, { CHARACTER_FACE_COLORS } from '@/components/game3d/CharacterShowcase3D';
import { TILE_COLORS, ENEMY_COLORS, COLLECTIBLE_COLORS } from '@/components/game3d/constants';
import { PROPS_CATALOG } from '@/types/props';
import type { CharacterProp } from '@/types/props';
import { RoundedBox } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';

interface ThreeDShowcaseScreenProps {
  onGoHome: () => void;
}

// ---------- Showcase tile samples ----------

function ShowcaseTile({ type, position, label }: { type: string; position: [number, number, number]; label: string }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.PointLight>(null);

  const colors = (() => {
    switch (type) {
      case 'start': return { top: TILE_COLORS.start.top, glow: TILE_COLORS.start.glow };
      case 'end': return { top: TILE_COLORS.end.top, glow: TILE_COLORS.end.glow };
      case 'lava': return { top: TILE_COLORS.lava.top, glow: TILE_COLORS.lava.glow };
      case 'stop': return { top: TILE_COLORS.stop.top, glow: TILE_COLORS.stop.glow };
      case 'teleporter': return { top: TILE_COLORS.teleporter.top, glow: TILE_COLORS.teleporter.glow };
      default: return { top: TILE_COLORS.wall.top, glow: '#999' };
    }
  })();

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();
    if (type === 'lava') {
      meshRef.current.position.y = position[1] + Math.sin(t * 3) * 0.03;
    }
    if (glowRef.current && (type === 'end' || type === 'lava' || type === 'teleporter')) {
      glowRef.current.intensity = 0.3 + Math.sin(t * 2) * 0.2;
    }
  });

  return (
    <group position={position}>
      <RoundedBox ref={meshRef} args={[0.8, 0.15, 0.8]} radius={0.03} smoothness={2} castShadow>
        <meshStandardMaterial
          color={colors.top}
          emissive={colors.glow}
          emissiveIntensity={type === 'lava' ? 0.5 : 0.2}
          metalness={type === 'stop' ? 0.3 : 0.1}
          roughness={type === 'stop' ? 0.1 : 0.4}
          transparent={type === 'stop'}
          opacity={type === 'stop' ? 0.85 : 1}
        />
      </RoundedBox>
      <pointLight ref={glowRef} position={[0, 0.2, 0]} color={colors.glow} intensity={0.3} distance={1.5} />
      <Text position={[0, -0.2, 0]} fontSize={0.09} color="white" anchorX="center" anchorY="top" outlineWidth={0.005} outlineColor="black">
        {label}
      </Text>
    </group>
  );
}

// ---------- Enemy showcase ----------

function ShowcaseEnemy({ type, position, label }: { type: string; position: [number, number, number]; label: string }) {
  const groupRef = useRef<THREE.Group>(null);
  const colors = ENEMY_COLORS[type as keyof typeof ENEMY_COLORS] || ENEMY_COLORS.beetle;

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y = Math.sin(clock.getElapsedTime()) * 0.5;
    groupRef.current.position.y = position[1] + Math.sin(clock.getElapsedTime() * 2) * 0.05;
  });

  const size = type === 'mite' ? 0.18 : type === 'drone' ? 0.15 : 0.22;
  const height = type === 'drone' ? 0.4 : type === 'mite' ? 0.18 : 0.25;

  return (
    <group position={position}>
      <group ref={groupRef}>
        {type === 'mite' ? (
          <mesh castShadow>
            <sphereGeometry args={[size, 12, 12]} />
            <meshStandardMaterial color={colors.body} metalness={0.3} roughness={0.4} />
          </mesh>
        ) : type === 'mirror' ? (
          <mesh castShadow>
            <octahedronGeometry args={[size, 0]} />
            <meshStandardMaterial color={colors.body} metalness={0.7} roughness={0.1} />
          </mesh>
        ) : (
          <RoundedBox args={[size * 2, height, size * 1.5]} radius={0.03} smoothness={2} castShadow>
            <meshStandardMaterial color={colors.body} metalness={0.2} roughness={0.5} />
          </RoundedBox>
        )}
        {/* Eyes */}
        <mesh position={[-size * 0.35, height * 0.15, size * 0.7]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshBasicMaterial color={colors.eye} />
        </mesh>
        <mesh position={[size * 0.35, height * 0.15, size * 0.7]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshBasicMaterial color={colors.eye} />
        </mesh>
      </group>
      <Text position={[0, -0.35, 0]} fontSize={0.08} color="white" anchorX="center" anchorY="top" outlineWidth={0.005} outlineColor="black">
        {label}
      </Text>
    </group>
  );
}

// ---------- Collectible showcase ----------

function ShowcaseCollectible({ type, position, label }: { type: 'coin' | 'gem'; position: [number, number, number]; label: string }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const color = type === 'coin' ? COLLECTIBLE_COLORS.coin : COLLECTIBLE_COLORS.gem;

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.y = clock.getElapsedTime() * 2;
    meshRef.current.position.y = position[1] + 0.1 + Math.sin(clock.getElapsedTime() * 3) * 0.05;
  });

  return (
    <group position={position}>
      <mesh ref={meshRef} castShadow>
        {type === 'coin' ? (
          <cylinderGeometry args={[0.15, 0.15, 0.05, 16]} />
        ) : (
          <octahedronGeometry args={[0.13, 0]} />
        )}
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} metalness={0.6} roughness={0.2} />
      </mesh>
      <pointLight position={[0, 0.15, 0]} color={color} intensity={0.3} distance={1} />
      <Text position={[0, -0.2, 0]} fontSize={0.08} color="white" anchorX="center" anchorY="top" outlineWidth={0.005} outlineColor="black">
        {label}
      </Text>
    </group>
  );
}

// ---------- Section label ----------

function SectionLabel({ text, position }: { text: string; position: [number, number, number] }) {
  return (
    <Text
      position={position}
      fontSize={0.18}
      color="#aaccff"
      anchorX="center"
      anchorY="middle"
      outlineWidth={0.008}
      outlineColor="#112244"
      fontWeight={700}
    >
      {text}
    </Text>
  );
}

// ---------- 3D Scene Content ----------

function ShowcaseScene({ selectedProps }: { selectedProps: CharacterProp[] }) {
  const characterIds = Object.keys(CHARACTER_FACE_COLORS);
  const enemyTypes = Object.keys(ENEMY_COLORS);
  const tileTypes = ['start', 'end', 'lava', 'stop', 'teleporter', 'wall'];
  const tileLabels = ['Start', 'Exit', 'Lava', 'Ice/Stop', 'Teleporter', 'Wall'];

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.5} color="#dde8ff" />
      <directionalLight position={[5, 8, 5]} intensity={0.7} castShadow shadow-mapSize={[1024, 1024]} />
      <directionalLight position={[-3, 5, -3]} intensity={0.2} />
      <hemisphereLight color="#87ceeb" groundColor="#2a2a3a" intensity={0.3} />

      {/* Ground */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]} receiveShadow>
        <planeGeometry args={[30, 30]} />
        <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
      </mesh>

      {/* ===== CHARACTERS ===== */}
      <SectionLabel text="Characters" position={[0, 1.2, -1]} />
      {characterIds.map((id, i) => {
        const x = (i - (characterIds.length - 1) / 2) * 1.5;
        const names: Record<string, string> = {
          default_hero: 'Hero (Pink)',
          tiny_blue: 'Tiny Blue',
          chonky_green: 'Chonky Green',
          sleek_purple: 'Sleek Purple',
          golden_glow: 'Golden Glow',
        };
        return (
          <ShowcaseCharacter3D
            key={id}
            characterId={id}
            position={[x, 0, -1]}
            rotating
            expression={i % 3 === 0 ? 'happy' : i % 3 === 1 ? 'neutral' : 'shocked'}
            props={i === 0 ? selectedProps : []}
            label={names[id] || id}
          />
        );
      })}

      {/* ===== TILES ===== */}
      <SectionLabel text="Tile Types" position={[0, 0.8, 1.5]} />
      {tileTypes.map((type, i) => {
        const x = (i - (tileTypes.length - 1) / 2) * 1.2;
        return (
          <ShowcaseTile key={type} type={type} position={[x, 0, 1.5]} label={tileLabels[i]} />
        );
      })}

      {/* ===== ENEMIES ===== */}
      <SectionLabel text="Enemies" position={[0, 0.8, 3.5]} />
      {enemyTypes.map((type, i) => {
        const x = (i - (enemyTypes.length - 1) / 2) * 1.3;
        const labels: Record<string, string> = {
          beetle: 'Bramble Beetle',
          drone: 'Tunnel Drone',
          mite: 'Skid Mite',
          hound: 'Cinder Hound',
          mirror: 'Echo Mirror',
        };
        return (
          <ShowcaseEnemy key={type} type={type} position={[x, 0, 3.5]} label={labels[type] || type} />
        );
      })}

      {/* ===== COLLECTIBLES ===== */}
      <SectionLabel text="Collectibles" position={[0, 0.8, 5.2]} />
      <ShowcaseCollectible type="coin" position={[-0.6, 0, 5.2]} label="Coin" />
      <ShowcaseCollectible type="gem" position={[0.6, 0, 5.2]} label="Gem" />
    </>
  );
}

// ---------- Props Selector (DOM overlay) ----------

function PropSelector({
  selectedProps,
  onToggleProp,
}: {
  selectedProps: string[];
  onToggleProp: (propId: string) => void;
}) {
  const propsBySlot = useMemo(() => {
    const groups: Record<string, CharacterProp[]> = {};
    for (const prop of PROPS_CATALOG) {
      if (!groups[prop.slot]) groups[prop.slot] = [];
      groups[prop.slot].push(prop);
    }
    return groups;
  }, []);

  const rarityColors: Record<string, string> = {
    common: '#aaa',
    rare: '#4a9eff',
    epic: '#b44aff',
    legendary: '#ffa500',
  };

  return (
    <div style={{
      position: 'absolute',
      right: 12,
      top: 60,
      width: 220,
      maxHeight: 'calc(100vh - 120px)',
      overflowY: 'auto',
      background: 'rgba(10, 10, 30, 0.85)',
      borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.15)',
      padding: 12,
      zIndex: 100,
      pointerEvents: 'auto',
    }}>
      <div style={{ color: '#aaccff', fontSize: 13, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Props
      </div>
      {Object.entries(propsBySlot).map(([slot, props]) => (
        <div key={slot} style={{ marginBottom: 10 }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
            {slot}
          </div>
          {props.map(prop => {
            const isSelected = selectedProps.includes(prop.id);
            return (
              <motion.button
                key={prop.id}
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={() => onToggleProp(prop.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '5px 8px',
                  marginBottom: 3,
                  borderRadius: 6,
                  border: isSelected ? `1.5px solid ${prop.model.glowColor || prop.model.color}` : '1px solid rgba(255,255,255,0.1)',
                  background: isSelected ? 'rgba(255,255,255,0.08)' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'white',
                  fontSize: 11,
                }}
              >
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: prop.model.color,
                  boxShadow: isSelected ? `0 0 6px ${prop.model.glowColor || prop.model.color}` : 'none',
                  flexShrink: 0,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {prop.name}
                  </div>
                  <div style={{ fontSize: 9, color: rarityColors[prop.rarity], textTransform: 'uppercase' }}>
                    {prop.rarity}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------- Main Screen ----------

export default function ThreeDShowcaseScreen({ onGoHome }: ThreeDShowcaseScreenProps) {
  const webglAvailable = useMemo(() => isWebGLAvailable(), []);
  const [selectedPropIds, setSelectedPropIds] = useState<string[]>([]);

  const selectedProps = useMemo(
    () => PROPS_CATALOG.filter(p => selectedPropIds.includes(p.id)),
    [selectedPropIds],
  );

  const toggleProp = (propId: string) => {
    const prop = PROPS_CATALOG.find(p => p.id === propId);
    if (!prop) return;
    setSelectedPropIds(prev => {
      if (prev.includes(propId)) return prev.filter(id => id !== propId);
      // Only one prop per slot
      return [...prev.filter(id => {
        const existing = PROPS_CATALOG.find(p => p.id === id);
        return existing?.slot !== prop.slot;
      }), propId];
    });
  };

  if (!webglAvailable) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#777', fontFamily: 'monospace' }}>
        WebGL required for 3D showcase.
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0a1a' }}>
      {/* Back button */}
      <motion.button
        type="button"
        whileTap={{ scale: 0.9 }}
        onClick={onGoHome}
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 100,
          padding: '8px 16px',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.2)',
          background: 'rgba(10,10,30,0.8)',
          color: 'white',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}
      >
        Back
      </motion.button>

      {/* Title */}
      <div style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        color: '#aaccff',
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        pointerEvents: 'none',
        textShadow: '0 2px 8px rgba(0,0,0,0.5)',
      }}>
        3D Asset Showcase
      </div>

      {/* Props selector */}
      <PropSelector selectedProps={selectedPropIds} onToggleProp={toggleProp} />

      {/* 3D Canvas */}
      <Canvas
        dpr={[1, 2]}
        camera={{ position: [0, 3, 8], fov: 40, near: 0.1, far: 100 }}
        gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
        shadows
        style={{ width: '100%', height: '100%' }}
      >
        <Suspense fallback={null}>
          <OrbitControls
            enableZoom={true}
            enablePan={true}
            enableRotate={true}
            minDistance={3}
            maxDistance={20}
            minPolarAngle={Math.PI * 0.1}
            maxPolarAngle={Math.PI * 0.48}
            enableDamping
            dampingFactor={0.08}
          />
          <ShowcaseScene selectedProps={selectedProps} />
        </Suspense>
      </Canvas>
    </div>
  );
}
